import React, {useEffect, useState} from 'react';
import {FileText, Loader2} from 'lucide-react';
import {api, apiError} from '../lib/api';
import {saveBlob} from '../lib/download';

const rasterTypes = new Set(['image/png', 'image/jpeg']);
const fileEndpoint = path => `/files/${String(path).split('/').map(encodeURIComponent).join('/')}`;

export async function downloadProtectedFile(file) {
  const {data} = await api.get(fileEndpoint(file.path), {responseType: 'blob'});
  // Downloads never navigate to active content in the application's origin.
  saveBlob(new Blob([data], {type: 'application/octet-stream'}), file.filename || 'allegato');
}

export function ProtectedFile({file, alt, className = 'block', imageClassName = 'h-32 w-full object-cover', label}) {
  const [preview, setPreview] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const path = file?.path;
  const image = rasterTypes.has(file?.content_type);

  useEffect(() => {
    if (!path || !image) return;
    let active = true, objectUrl;
    const controller = new AbortController();
    setPreview(null);
    api.get(fileEndpoint(path), {responseType: 'blob', signal: controller.signal}).then(({data}) => {
      if (!active) return;
      if (!rasterTypes.has(data.type?.split(';')[0].toLowerCase())) {
        setPreview({path, error: 'Anteprima non disponibile. Puoi scaricare il file.'});
        return;
      }
      objectUrl = URL.createObjectURL(data);
      setPreview({path, url: objectUrl});
    }).catch(error => {
      if (active) setPreview({path, error: apiError(error)});
    });
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, image]);

  const current = preview?.path === path ? preview : null;
  if (image && current?.url) return <a href={current.url} target="_blank" rel="noopener noreferrer" className={className} aria-label={label || `Apri ${file.filename || 'immagine'}`}>
    <img loading="lazy" src={current.url} alt={alt || file.filename || 'Allegato'} className={imageClassName}/>
    {label && <span className="inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline">{label}</span>}
  </a>;
  if (image && !current) return <div role="status" className="flex min-h-24 items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true"/>Caricamento immagine…</div>;
  return <div>
    {current?.error && <p role="alert" className="px-2 py-2 text-sm text-muted-foreground">{current.error}</p>}
    <button type="button" disabled={downloadBusy || !path} className={`min-h-24 w-full ${className}`} onClick={async () => {
      setDownloadBusy(true);setDownloadError('');
      try {await downloadProtectedFile(file);} catch (error) {setDownloadError(apiError(error));}
      finally {setDownloadBusy(false);}
    }}>
      <span className="flex flex-col items-center justify-center p-3 text-muted-foreground"><FileText className="h-7 w-7" aria-hidden="true"/><span className="mt-1 max-w-full truncate text-xs">{downloadBusy ? 'Download…' : `Scarica ${file?.filename || 'allegato'}`}</span></span>
    </button>
    {downloadError && <p role="alert" className="p-2 text-sm text-red-300">{downloadError}</p>}
  </div>;
}
